import { fetchWithCometFallback, MetricsLambdaFetch } from '../../../../utils/fetch-utils';
import {
  AttributionTypeConst,
  CPTBRow,
  DashboardData,
  MarketData,
  MarketRow,
  MarketSalesRow
} from '../dtc-commerce-mmm-types';
import { CsvRow, LoadContext, loadRows, parseNum } from './dtc-commerce-mmm-data-loader';

const featureWeekKey = (r: { date: string; name: string }): string => `${r.name}|${r.date}`;

/**
 * geo_spend_effect_over_time is long over attribution_type × estimate_type: one row each for
 * the mean and both ends of the range. Folds them into one row per market, week and feature,
 * per attribution type. Rows for anything that isn't paid media (each DMA's Covariates row) drop.
 */
export const parseMarketRows = (
  rows: CsvRow[],
  paidMediaNames: ReadonlySet<string>
): { actualized: MarketRow[]; raw: MarketRow[] } => {
  const byAttribution = {
    [AttributionTypeConst.ACTUALIZED]: new Map<string, MarketRow>(),
    [AttributionTypeConst.RAW]: new Map<string, MarketRow>()
  };
  rows.forEach(r => {
    if (!paidMediaNames.has(r.name)) return;
    if (
      r.attribution_type !== AttributionTypeConst.ACTUALIZED &&
      r.attribution_type !== AttributionTypeConst.RAW
    ) {
      return;
    }
    const rowsByKey = byAttribution[r.attribution_type];
    const key = `${r.geo}|${r.date}|${r.name}`;
    const row = rowsByKey.get(key) ?? {
      geo: r.geo,
      date: r.date,
      name: r.name,
      spend: 0,
      contribution: 0,
      ciLo: 0,
      ciHi: 0
    };
    if (r.estimate_type === 'mean') {
      row.spend = parseNum(r.spend);
      row.contribution = parseNum(r.contribution);
    } else if (r.estimate_type === 'ci_lo') {
      row.ciLo = parseNum(r.contribution);
    } else if (r.estimate_type === 'ci_hi') {
      row.ciHi = parseNum(r.contribution);
    }
    rowsByKey.set(key, row);
  });
  return {
    actualized: Array.from(byAttribution[AttributionTypeConst.ACTUALIZED].values()),
    raw: Array.from(byAttribution[AttributionTypeConst.RAW].values())
  };
};

/**
 * The market file carries each DMA's contribution as the model estimated it, while every other
 * panel shows contribution normalized to observed sales. Scales each feature-week's market rows
 * so they add up to the national figure the rest of the page shows, so market totals match
 * Channel Performance. On DSW the two differ by 0.16%. A feature-week with no national row, or
 * no market contribution to scale, is left as it is.
 */
export const reconcileToNational = (rows: MarketRow[], national: CPTBRow[]): MarketRow[] => {
  const nationalByKey = new Map(national.map(r => [featureWeekKey(r), r.effectiveNormalized]));
  const marketTotals = new Map<string, number>();
  rows.forEach(r => {
    const key = featureWeekKey(r);
    marketTotals.set(key, (marketTotals.get(key) ?? 0) + r.contribution);
  });
  return rows.map(r => {
    const key = featureWeekKey(r);
    const target = nationalByKey.get(key);
    const total = marketTotals.get(key) ?? 0;
    if (target === undefined || total === 0) return r;
    const scale = target / total;
    return {
      ...r,
      contribution: r.contribution * scale,
      ciLo: r.ciLo * scale,
      ciHi: r.ciHi * scale
    };
  });
};

// Each market's observed sales by week, from the per-DMA actual-vs-predicted file.
export const parseMarketSales = (rows: CsvRow[]): MarketSalesRow[] =>
  rows
    .filter(
      r => r.geo && r.geo !== 'national_geo' && (!r.estimate_type || r.estimate_type === 'mean')
    )
    .map(r => ({ geo: r.geo, date: r.date || '', actualKpi: parseNum(r.actual_kpi) }));

export const parseMarketAccuracy = (rows: CsvRow[]): Record<string, number> =>
  Object.fromEntries(rows.filter(r => r.geo).map(r => [r.geo, parseNum(r.mape)]));

// Every market × week × feature: too big for a plain lambda response on larger clients, so it
// falls back to a Comet self-invoke, as the old Geo MMM page does.
const fetchMarketSpendEffectRows = (ctx: LoadContext): Promise<CsvRow[]> =>
  fetchWithCometFallback<Record<string, string>, CsvRow[]>({
    service: MetricsLambdaFetch,
    standardEndpoint: '/getGeoSpendEffectOverTimeData',
    input: { company: ctx.client, branch: ctx.branch, kpi: ctx.kpi, date: ctx.timestamp },
    method: 'get'
  });

/**
 * Each market's rows for a run whose national data is already loaded: the paid-media list and the
 * national figures to reconcile to come from it. Loaded apart from the national data, so a slow
 * or failing market file can't hold up the rest of the page.
 */
export const loadMarketData = async (
  ctx: LoadContext,
  data: DashboardData
): Promise<MarketData> => {
  const [spendEffectRows, actualVsPredictedRows, accuracyRows] = await Promise.all([
    fetchMarketSpendEffectRows(ctx),
    loadRows(ctx, '/getActualVsPredictedData'),
    loadRows(ctx, '/getMapeByGeoData')
  ]);
  const paidMediaNames = new Set(data.channelHierarchy.channels.map(c => c.name));
  const { actualized, raw } = parseMarketRows(spendEffectRows, paidMediaNames);
  return {
    actualized: reconcileToNational(actualized, data.actualized.cptb),
    raw: reconcileToNational(raw, data.raw.cptb),
    sales: parseMarketSales(actualVsPredictedRows),
    accuracy: parseMarketAccuracy(accuracyRows)
  };
};
